import { QueryClient } from '@tanstack/react-query';

/**
 * Query retry policy. A timeout/transport failure has no HTTP `status`; before client-side timeouts
 * existed it was retried the full 2× against an unreachable backend, stretching the "Signing you in…"
 * splash for minutes. Now such errors (tagged `kind`) get ONE quick retry (covers a blip) then stop;
 * 401/403/404 never retry; everything else keeps the default 2 retries.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const kind = (error as { kind?: string } | null)?.kind;
  if (kind === 'timeout' || kind === 'network') return failureCount < 1;
  if (failureCount >= 2) return false;
  const status = (error as { status?: number } | null)?.status;
  if (status === 401 || status === 403 || status === 404) return false;
  return true;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Refetch when the user tabs back. Acts as a backstop for any SignalR push that didn't
      // reach the tab (network blips, hub mid-reconnect during the publish, etc.). Cheap with
      // the staleTime above — focus events only refetch what's been stale for >30s.
      refetchOnWindowFocus: true,
      // Also refetch when the network reconnects, for the same reason.
      refetchOnReconnect: true,
      retry: shouldRetryQuery,
    },
    mutations: { retry: false },
  },
});
